import {
    answerRenderablePayloadFingerprint,
    isAnswerStubCacheEntry,
    isHydratedAnswerCacheEntry,
} from 'state/cache'

// Richest-seen latch: quests store only sorted_answer_ids, so answer CONTENT lives in the
// LRU-bounded entity cache (ENTITY_CACHE_LIMITS.answer). A long scroll session evicts answers
// that still back mounted/near-viewport rows; on the next render those rows resolve to a lean
// reconstruction (placeholder stub, or a variant without images_v2) until the hydration lane
// refetches — so payload sections flicker out and back, and row heights oscillate by the media
// height. The latch keeps the last hydrated variant rendered per answer id so rows never
// visually regress under cache pressure. A candidate with a NEWER updated_at always wins even
// if leaner (real edits/deletions must propagate); eviction artifacts carry no updated_at.
const MAX_LATCHED_ANSWERS = 600

const latchedAnswers = new Map()
const retainedFullAnswerIds = new Set()

const latchTime = answer => {
    const time = Date.parse(answer?.updated_at || '')
    return Number.isFinite(time) ? time : 0
}

const rememberLatchedAnswer = answer => {
    const id = answer?.id ? String(answer.id) : null
    if (!id) return

    // Delete before set so Map iteration order stays least-recent-first for LRU eviction.
    latchedAnswers.delete(id)
    latchedAnswers.set(id, answer)
    while (latchedAnswers.size > MAX_LATCHED_ANSWERS) {
        const oldestKey = latchedAnswers.keys().next().value
        if (oldestKey === undefined) break
        latchedAnswers.delete(oldestKey)
        retainedFullAnswerIds.delete(oldestKey)
    }
}

export const retainAnswerForRemount = answer => {
    rememberLatchedAnswer(answer)
    if (answer?.id) retainedFullAnswerIds.add(String(answer.id))
}

export const hasRetainedFullAnswerBody = answerId => {
    const id = answerId == null ? null : String(answerId)
    if (!id || !retainedFullAnswerIds.has(id)) return false

    return latchedAnswers.get(id)?.body_truncated !== true
}

const latchedAnswerFor = (...candidates) => {
    for (const candidate of candidates) {
        const id = candidate?.id ? String(candidate.id) : null
        if (!id) continue
        const held = latchedAnswers.get(id)
        if (held) return held
    }
    return null
}

const newerCandidateThanLatch = (...candidates) => {
    for (const candidate of candidates) {
        const id = candidate?.id ? String(candidate.id) : null
        if (!id) continue
        const held = latchedAnswers.get(id)
        if (!held || latchTime(candidate) <= latchTime(held)) continue

        // Newness is not authority: updated_at also bumps on touch and on sidebar-activity
        // fan-out invalidations, whose payloads are LEAN — accepting them here renders a
        // stripped answer for a frame and reintroduces the flip this latch exists to stop.
        // Real edits ship hydrated payloads carrying delta_json; deletes are marked deleted.
        // Require one of those authority signals before displacing a fuller held variant.
        if (!isHydratedAnswerCacheEntry(candidate)) continue
        if (!candidate.deleted && held.delta_json && !candidate.delta_json) continue

        return candidate
    }
    return null
}

const lostRenderablePayload = (held, candidate) => {
    const candidatePayload = new Set(answerRenderablePayloadFingerprint(candidate))
    return answerRenderablePayloadFingerprint(held).some(field => !candidatePayload.has(field))
}

const lostRenderableText = (held, candidate) => {
    const richTextLength = answer => {
        const deltaLength = [answer?.delta_json, answer?.nested_delta_json]
            .flatMap(delta => (Array.isArray(delta?.ops) ? delta.ops : []))
            .reduce(
                (length, op) => length + (typeof op?.insert === 'string' ? op.insert.length : 0),
                0,
            )

        return Math.max(
            String(answer?.content || '').length,
            String(answer?.display_content || '').length,
            deltaLength,
        )
    }

    const heldTextLength = richTextLength(held)
    const candidateTextLength = richTextLength(candidate)
    return heldTextLength > 0 && candidateTextLength < heldTextLength
}

const lostFullBody = (held, candidate) =>
    held?.body_truncated !== true && candidate?.body_truncated === true

export function clearLatchedAnswersForTest() {
    latchedAnswers.clear()
    retainedFullAnswerIds.clear()
}

export function resolveAnswerInViewAnswer({ cachedAnswer, propAnswer }) {
    const newerCandidate = newerCandidateThanLatch(cachedAnswer, propAnswer)
    if (newerCandidate) {
        rememberLatchedAnswer(newerCandidate)
        return newerCandidate
    }

    let resolved = null
    if (isHydratedAnswerCacheEntry(cachedAnswer)) {
        resolved = cachedAnswer
    } else if (isAnswerStubCacheEntry(cachedAnswer) && isHydratedAnswerCacheEntry(propAnswer)) {
        resolved = propAnswer
    } else if (isHydratedAnswerCacheEntry(propAnswer)) {
        resolved = propAnswer
    }

    if (resolved) {
        const held = latchedAnswerFor(resolved)
        // A variant that lost renderable payload vs what we already showed (an eviction-era
        // reconstruction without images_v2, preview-length text instead of the full body, or a
        // touch/fan-out artifact with a bumped updated_at): keep the held one. A newer
        // timestamp only earns displacement when the payload is authoritative — real edits carry
        // delta_json, and deletes are marked deleted (or arrive via the fast-path above).
        if (held && held !== resolved) {
            const isAuthoritativeNewer =
                latchTime(resolved) > latchTime(held) &&
                (resolved.deleted || !(held.delta_json && !resolved.delta_json))
            if (
                !isAuthoritativeNewer &&
                (lostRenderablePayload(held, resolved) ||
                    lostRenderableText(held, resolved) ||
                    lostFullBody(held, resolved))
            ) {
                return held
            }
        }
        rememberLatchedAnswer(resolved)
        return resolved
    }

    // Nothing hydrated this tick (typically an evicted answer resolving to a bare
    // placeholder): keep rendering the last hydrated variant instead of regressing.
    const held = latchedAnswerFor(cachedAnswer, propAnswer)
    if (held) return held

    return cachedAnswer || propAnswer || null
}
